// Types of action taken by AI
export const actionTypes = {
  word: 'word',
  phrase: 'phrase',
  WORD_PREP: 'WORD_PREP', // currently, calculating pause time
};

//Types of ending errors in a word
export const wordEndingErrorType = {
  ADDED: 'added',
  DROPPED: 'dropped',
  MIX: 'mix',
};

export const NO_RESPONSE = 'NO_RESPONSE_EMPTY';

//Types of Users
export const userTypes = {
  TEACHER: 'teacher',
  STUDENT: 'student',
  SCHOOL_ADMIN: 'SCHOOL_ADMIN',
  DISTRICT_ADMIN: 'DISTRICT_ADMIN',
};

//Type of Questions, used for Quiz Component
export const QUESTION_TYPE = {
  CLOZE: 'cloze',
  MULTI: 'multi',
};

// Benchmark Report - Filter
export const ASSESSMENT_TYPE_FILTERS = {
  BENCHMARK_ONLY: "BENCHMARK_ONLY",
  MOST_RECENT_ASSESSMENT: "MOST_RECENT_ASSESSMENT",
}

// Reader Manager Reading Modes
export const READING_MODES = {
  TUTOR: "tutor",
  ASSESSMENT: "assessment",
}

//Type of Assignment
export const assignmentTypes = {
  ASSESSMENT: 'assessment',
  TUTOR: 'tutor',
  DYSLEXIA: 'dyslexia',
  NWF: 'nwf',
  SPELLING: 'spelling',
  LISTENING_COMPREHENSION: 'listeningComprehension',
  READING_COMPREHENSION: 'readingComprehension',
  VOCABULARY_SCREENER: 'vocabularyScreener',
  LETTER_NAMING_AND_SOUNDS: 'letterNamingAndSounds',
};

//Type of Assignment
export const aosTaskTypes = {
  ran: 'ran',
  wordPartDeletion: 'wordPartDeletion',
  upAndDown: 'upAndDown',
  changeOnePart: 'changeOnePart',
  nonsenseWordRepetition: 'nonsenseWordRepetition',
  blending: 'blending',
  retell: 'retell',
  spelling: 'spelling',
  readingComprehension: 'readingComprehension',
  wif: 'wif',
  nwf: 'nwf',
  assessment: 'assessment',
  vocabulary: 'vocabulary',
  writtenCommunication: 'writtenCommunication',
  letterName: 'letterName',
  letterSound: 'letterSound',
  beginningLetterSound: 'beginningLetterSound',
  listeningComprehension: 'listeningComprehension',
  graphemePhonemeConversion: 'graphemePhonemeConversion',
  letterNamingAndSounds: 'letterNamingAndSounds',
  freeAnswer: 'freeAnswer',
  formative: 'formative',
};

export const phraseTypes = {
  NONSENSE_WORD_REPETITION: 'nonsenseWordRepetition',
  CHANGE_ONE_PART: 'changeOnePart',
  WORD_PART_DELETION: 'wordPartDeletion',
  UP_AND_DOWN: 'upAndDown',
  LETTER_FLIES: 'letterFlies',
  ELKONIN: 'elkonin',
  SPELLING: 'spelling',
  LISTENING_COMPREHENSION: 'listeningComprehension',
  SENTENCE_BUILDER: 'sentenceBuilder',
  VOCABULARY: 'vocabulary',
  GRAPHEME_PHONEME_CONVERSION: 'graphemePhonemeConversion',
  MMC: 'mmc',
  FREE_ANSWER: 'freeAnswer',
  BUCKETING: 'bucketing',
  SEQUENCING: 'sequencing',
}

// Assessment types with structured instruction handling: separate written/verbal instructions and testlet-level FTUE.
// ReaderManager uses this to show written instructions on repeat button.
// AssessmentOrchestrationService uses this to split testlet-level FTUE from item-level instructions.
// For written-communication assessments the testlet-level type ('writtenCommunication') and
// item-level type ('sentenceBuilder') diverge — use the variant that matches your caller's input.
export const hasStructuredInstructionsForTestletType = (type) => {
  return (
    type === aosTaskTypes.listeningComprehension
    || type === aosTaskTypes.writtenCommunication
    || type === aosTaskTypes.vocabulary
    || type === aosTaskTypes.graphemePhonemeConversion
    || type === aosTaskTypes.freeAnswer
    || type === aosTaskTypes.formative
  );
}

export const FORMATIVE_ITEM_TYPES = [
  phraseTypes.MMC,
  phraseTypes.FREE_ANSWER,
  phraseTypes.BUCKETING,
  phraseTypes.SEQUENCING,
];

/**
 * The item types that only ever appear in Formative content.
 *
 * Deliberately excludes MMC: plain `mmc` is also the item type of legacy Spanish blending and
 * pictureComprehension items, so treating it as a Formative signal would pull those testlets
 * onto the Formative path.
 */
export const FORMATIVE_ONLY_ITEM_TYPES = [
  phraseTypes.FREE_ANSWER,
  phraseTypes.BUCKETING,
  phraseTypes.SEQUENCING,
];

/**
 * Whether a testlet is a Formative suite.
 *
 * Four signals, because no one of them covers every case.
 *
 * type catches the mocks, which name the testlet "formative" outright. The platform never
 * does: AOS serves a testlet under the task type its blueprint asks for, and that has to be
 * one SRS registers as an activity type - multipleChoice, spelling, wif.
 *
 * subType is how a served suite is meant to be recognised. It is the field a blueprint already
 * classifies its testlets with, so it answers "which suite" while type answers "which
 * activity", and neither has to lie for the other. It reads null on every committed testlet
 * today because no blueprint sets it yet - this clause is what those blueprints will hit.
 *
 * The item-type clause is a fallback for the suite's own interaction types, which appear in
 * no other bank. It cannot cover a Multiple Choice suite: those items are plain mmc, which
 * is also the item type of legacy Spanish blending and pictureComprehension, so listing it
 * would pull those testlets onto the Formative path.
 *
 * The checkpoint tag is what identifies a served Multiple Choice or vocabulary suite today.
 * Every Formative testlet carries one (cp_<format>_<domain>_<subtest>_<grade>_<unit>),
 * because the blueprint builder joins testlets to checkpoints by it, and no other bank uses
 * the prefix.
 */
export const FORMATIVE_CHECKPOINT_TAG_PREFIX = 'cp_';

const hasFormativeCheckpointTag = (testlet) =>
  (testlet?.tags || []).some((tag) => String(tag).startsWith(FORMATIVE_CHECKPOINT_TAG_PREFIX));

export const isFormativeTestlet = (testlet) => (
  testlet?.type === aosTaskTypes.formative
  || String(testlet?.subType || '').trim().toLowerCase() === aosTaskTypes.formative
  || hasFormativeCheckpointTag(testlet)
  || (testlet?.items || []).some((item) => FORMATIVE_ONLY_ITEM_TYPES.includes(item?.type))
);

export const ENCODED_RESPONSE_ITEM_TYPES = [
  phraseTypes.BUCKETING,
  phraseTypes.SEQUENCING,
];

const STRUCTURED_INSTRUCTION_ITEM_TYPES = [
  phraseTypes.LISTENING_COMPREHENSION,
  phraseTypes.SENTENCE_BUILDER,
  phraseTypes.VOCABULARY,
  phraseTypes.GRAPHEME_PHONEME_CONVERSION,
  ...FORMATIVE_ITEM_TYPES,
];

export const hasStructuredInstructionsForItemType = (type) =>
  STRUCTURED_INSTRUCTION_ITEM_TYPES.includes(type);

// Legacy triggers used by Spelling, Quiz, VisualAttention, and other existing components.
// New assessment components (MMC, SentenceBuilder) use the structured TriggerActions system below.
export const PhraseTriggers = {
  Submit: 'submit',
  Letters: 'letters',
  Repeat: 'repeat',
  Backspace: 'backspace',
  Start: 'start',
  Ready: 'ready',
  Split: 'split',
  ShowTargetAnimal: 'showTargetAnimal',
  ShowAllAnimals: 'showAllAnimals',
  HighlightReferenceStoryBtn: 'highlightReferenceStoryBtn',
}

// Full raw strings (bracket contents) that require onTriggerActionComplete before the next phrase.
// Empty by default — awaitables use isAwaitableIntroductionTrigger(triggerAction, triggerData).
// Repopulate here for legacy tokens that do not match that helper; Phrase.speakPhrases still ORs includes(trigger).
export const AWAITABLE_TRIGGERS = []

// Structured trigger system for assessment components (MMC, SentenceBuilder).
// Verbal instructions use format: [action:target1,target2] e.g. [show:options,submit]
// Phrase.js parses these into { action, targets } and passes them to components.
export const TriggerActions = {
  Start: 'start',
  Ready: 'ready',
  Show: 'show',
  Hide: 'hide',
  Highlight: 'highlight',
  Click: 'click',
  Play: 'play',
}

export const POOL_NARRATION_TARGETS = ['options'];
export const BOARD_TARGETS = ['board', 'track', 'positions'];
export const MEDIA_TARGETS = ['video', 'image'];

export const isTriggerTarget = (targets, target) => (targets || []).includes(target);

/**
 * The item types whose screen implements [play:*] -- ModularMultipleChoice, which renders the
 * shared MMC case, and useFormativeScreen, which renders Bucketing and Sequencing.
 *
 * Phrase must not park on a play trigger for anything else. The awaitable path clears the
 * verbal speech safeguard and only re-arms it from inside pendingTriggerCallback, so a screen
 * with no Play case never completes the trigger and the child is left with no way forward.
 */
const POOL_NARRATION_ITEM_TYPES = [
  phraseTypes.MMC,
  phraseTypes.GRAPHEME_PHONEME_CONVERSION,
  phraseTypes.VOCABULARY,
  phraseTypes.LISTENING_COMPREHENSION,
  phraseTypes.BUCKETING,
  phraseTypes.SEQUENCING,
];

export const screenNarratesPool = (itemType) => POOL_NARRATION_ITEM_TYPES.includes(itemType);

const scriptTriggerTargets = (verbalInstructions, action) => {
  const script = String(verbalInstructions || '');
  const targets = [];
  const tokenPattern = /\[([^\]]*)\]/g;
  let token = tokenPattern.exec(script);
  while (token !== null) {
    const raw = token[1];
    const colonIndex = raw.indexOf(':');
    if (colonIndex !== -1 && raw.slice(0, colonIndex).trim().toLowerCase() === action) {
      for (const target of raw.slice(colonIndex + 1).split(',')) {
        if (target.trim()) targets.push(target.trim());
      }
    }
    token = tokenPattern.exec(script);
  }
  return targets;
};

export const scriptPlaysTarget = (verbalInstructions, targets) =>
  scriptTriggerTargets(verbalInstructions, TriggerActions.Play)
    .some((target) => isTriggerTarget(targets, target));

export const scriptShowsTarget = (verbalInstructions, targets) =>
  scriptTriggerTargets(verbalInstructions, TriggerActions.Show)
    .some((target) => isTriggerTarget(targets, target));

/**
 * Span of the first `[action:target]` token naming one of `targets`, or null.
 * Callers that need the token's position — not just its presence — must use this
 * rather than indexOf, so casing and multi-target tokens parse the same as everywhere else.
 */
export const findTriggerToken = (verbalInstructions, action, targets) => {
  const script = String(verbalInstructions || '');
  const tokenPattern = /\[([^\]]*)\]/g;
  let token = tokenPattern.exec(script);
  while (token !== null) {
    const raw = token[1];
    const colonIndex = raw.indexOf(':');
    if (colonIndex !== -1 && raw.slice(0, colonIndex).trim().toLowerCase() === action) {
      const names = raw.slice(colonIndex + 1).split(',').map((name) => name.trim());
      if (names.some((name) => isTriggerTarget(targets, name))) {
        return { start: token.index, end: token.index + token[0].length };
      }
    }
    token = tokenPattern.exec(script);
  }
  return null;
};

export const deferPoolNarrationToScript = (verbalInstructions) =>
  scriptPlaysTarget(verbalInstructions, POOL_NARRATION_TARGETS);

/** Shallow-compare `currentTriggerData` arrays — Phrase may pass a new reference each render. */
export function triggerDataShallowEqual(a, b) {
  const aa = a || [];
  const bb = b || [];
  if (aa.length !== bb.length) return false;
  return aa.every((v, i) => v === bb[i]);
}

// Structured trigger actions that require the child to call onTriggerActionComplete.
// Highlight is NOT awaitable — pointer stays visible while speech continues, cleared by the next trigger.
export const AWAITABLE_TRIGGER_ACTIONS = [
  TriggerActions.Click,
]

/** True when Phrase must wait for the child before speaking the next instruction segment (see speakPhrases). */
export function isAwaitableIntroductionTrigger(triggerAction, triggerData, itemType) {
  if (AWAITABLE_TRIGGER_ACTIONS.includes(triggerAction)) return true;
  // [show:options] during FTUE: MMC reads options sequentially; Phrase must not advance until that finishes
  // (same pacing idea as BlendingSelection.playIntroductionSequence / playInstructions).
  if (triggerAction === TriggerActions.Show && (triggerData || []).some((t) => isTriggerTarget(POOL_NARRATION_TARGETS, t))) return true;
  // [show:repeatOverlayInput] after optional TTS: enable Replay/Next; Phrase waits for Next (see repeatOverlay — visual only).
  if (triggerAction === TriggerActions.Show && (triggerData || []).includes('repeatOverlayInput')) return true;
  // [play:options] narrates already-revealed options one at a time; Phrase waits for the last
  // one -- but only on a screen that implements Play. See POOL_NARRATION_ITEM_TYPES.
  if (
    triggerAction === TriggerActions.Play
    && screenNarratesPool(itemType)
    && (triggerData || []).some((t) => isTriggerTarget(POOL_NARRATION_TARGETS, t))
  ) return true;
  return false;
}

//Activity status
export const activityStatus = {
  ASSIGNED: 'assigned',
  SCORING_NOT_STARTED: 'not_started',
  SCORING_IN_PROGRESS: 'in_progress',
  SCORED: 'scored',
  RESCORED: 'rescored',
  ABORTED: 'aborted',
  COMPLETED: 'completed',
  UNDER_REVIEW: 'underReview',
  ARCHIVED: 'archived',
}

// Used for Diagnostic Report
export const SEQUENCE_STATUS = {
  NOT_STARTED: "NOT_STARTED",
  IN_PROGRESS: "IN_PROGRESS",
  NEXT_UP: "NEXT_UP",
  COMPLETED: "COMPLETED",
  ADDITIONAL_PRACTICE: "ADDITIONAL_PRACTICE",
};

// Type of Sequence for Diagnostic Report
export const STORY_SEQUENCE_TYPES = {
  ERSS: "ERSS",
};

// This constant influences available M/S/B.
// Most reports have type default.
// Currently, all admin reports only have ORF available.
// Dyslexia report has a unique M/S/B.
export const msbType = {
  DEFAULT: 'DEFAULT',
  ORF: 'ORF',
  DYSLEXIA: 'DYSLEXIA',
  COMPREHENSIVE: 'COMPREHENSIVE',
}

export const contentTags = {
  PICTURE_STORY: 'PICTURE_STORY',
  EARLY_READER_WORD_LIST: 'EARLY_READER_WORD_LIST',
  EARLY_READER_READ_WITH_ME: 'EARLY_READER_READ_WITH_ME',
  EARLY_READER_REPETITIVE_TEXT: 'EARLY_READER_REPETITIVE_TEXT',
  EARLY_READER_SEQUENCE_END: 'EARLY_READER_SEQUENCE_END',
  SAXON_SEQUENCE_END: 'SAXON_SEQUENCE_END',
  SAXON_STORY: 'hmh-saxon',
  CHANGE_ONE_PART: 'CHANGE_ONE_PART',
  WORD_PART_DELETION: 'WORD_PART_DELETION',
  EARLY_READER_CHANGE_ONE_PART: 'EARLY_READER_CHANGE_ONE_PART',
  EARLY_READER_UP_AND_DOWN: 'EARLY_READER_UP_AND_DOWN',
  EARLY_READER_LETTER_FLIES: 'EARLY_READER_LETTER_FLIES',
  EARLY_READER_ELKONIN_BOXES: 'EARLY_READER_ELKONIN_BOXES',
  EARLY_READER_ELKONIN: 'EARLY_READER_ELKONIN',
}

export const earlyReaderContentTagsNoEdm = [
  contentTags.EARLY_READER_LETTER_FLIES,
  contentTags.EARLY_READER_CHANGE_ONE_PART,
  contentTags.EARLY_READER_ELKONIN,
  contentTags.EARLY_READER_UP_AND_DOWN
]

export const recommendationModes = {
  SEQUENCE_LOCKED: 'SEQUENCE_LOCKED',
  SEQUENCE_EXPIRES: 'SEQUENCE_EXPIRES',
  NO_SEQUENCE: 'NO_SEQUENCE',
}

export const locales = {
  EN_US: 'EN-US',
  ES_MX: 'ES-MX',
}

export const localesUnderscoreMap = {
  "EN-US": "EN_US",
  "ES-MX": "ES_MX",
}

export const i18nLabels = {
  "en": "English",
  "es": "Spanish",
};

export const i18nOptions = [
  {value: "en", label: "English"},
  {value: "es", label: "Spanish"},
];

export const SPANISHCONFIG ={
  englishOnly: "ENGLISH_ONLY",
  spanishOnly: "SPANISH_ONLY",
  bothOneSession: "BOTH_ONE_SESSION",
  bothOneSessionEnglishFirst: "BOTH_ONE_SESSION_ENGLISH_FIRST",
  bothTwoSessions: "BOTH_TWO_SESSIONS",
  bothTwoSessionsEnglishFirst: "BOTH_TWO_SESSIONS_ENGLISH_FIRST"
}

// Map dimensions in world coordinates
export const mapSize = {
  width: 2048,
  height: 1152
};

export const gameSFXKeys = {
  bringThatStory: "bringThatStory",
  robotMovement: "robotMovement",
  storyCraftMovement: "storyCraftMovement",
  backgroundMusicDuel: "backgroundMusicDuel",
  backgroundMusicGeneral: "backgroundMusicGeneral",
  backgroundMusicStorybot: "backgroundMusicStorybot",
  robotStealSound: "robotStealSound",
  robotStoryAcquired: "robotStoryAcquired",
  lowWordPowerAlert: "lowWordPowerAlert",
  outOfWordPower: "outOfWordPower",
  villainEndDuel: "villainEndDuel",
  villianGotcha:"gotcha",
  villainLevelUp: "villainLevelUp",
  robotEndDuel: "robotEndDuel",
  wpMeterEndDuel: "wpMeterEndDuel",
  wpPhraseEarn: "wpPhraseEnd",
  danceMusic: "danceMusic",
  pickFromTray: "pickFromTray",
  addSticker: "addSticker"
}

export const AUDIO_STATUS = {
  audioPlaying: 2,
  audioStopped: 4,
};

export const StudentInboxAssignmentTypes = Object.freeze({
  ASSESSMENT: 'ASSESSMENT',
  CALIBRATION: 'CALIBRATION',
  BENCHMARK: 'BENCHMARK',
  SODA: 'SODA',
  PROGRESS_MONITORING: 'PROGRESS_MONITORING',
  FORMATIVE: 'FORMATIVE',
  INSTRUCT: 'INSTRUCT',
  CURRICULUM: 'CURRICULUM',
  TUTOR: 'TUTOR'
});

export const AssessmentAssignmentTypes = Object.freeze([
  StudentInboxAssignmentTypes.ASSESSMENT,
  StudentInboxAssignmentTypes.BENCHMARK,
  StudentInboxAssignmentTypes.CALIBRATION,
  StudentInboxAssignmentTypes.PROGRESS_MONITORING,
  StudentInboxAssignmentTypes.SODA,
  // FORMATIVE is AOS-driven like the rest of this list: isAssessmentTypeAssignment gates
  // both launching the assignment (App.js) and shouldUseAOS (util.js), so leaving it out
  // means the app fetches the assignment and then declines to run it.
  StudentInboxAssignmentTypes.FORMATIVE
]);

export const AssignmentTypesOrder = Object.freeze([
  StudentInboxAssignmentTypes.ASSESSMENT,
  StudentInboxAssignmentTypes.BENCHMARK,
  StudentInboxAssignmentTypes.CALIBRATION,
  StudentInboxAssignmentTypes.PROGRESS_MONITORING,
  StudentInboxAssignmentTypes.SODA,
  // After the screening assessments: a checkpoint assignment should never pre-empt a
  // benchmark. Absent from this list it sorts to -1, i.e. ahead of everything.
  StudentInboxAssignmentTypes.FORMATIVE,
  StudentInboxAssignmentTypes.INSTRUCT,
  StudentInboxAssignmentTypes.CURRICULUM,
  StudentInboxAssignmentTypes.TUTOR,
]);

export const EXTEND_ASSESSMENT_TIME_TAG = "EXTEND_ASSESSMENT_TIME";

// Window events for the TTS lifecycle the thinking bubble listens to (TT-1166).
// Centralized so the dispatch sites (LexaSpeech, Speech.abortSpeech) and the listener
// (ThinkingBubble) can never drift on a string literal without a compile-time reference.
export const TTS_EVENTS = Object.freeze({
  CALL_STARTED: 'ttsCallStartedEvent',
  REQUEST_ENDED: 'ttsRequestEndedEvent',
});

// Thinking-bubble (TT-1166): timing for the "thinking" thought-bubble shown above
// Amira's avatar while a TTS request is outstanding. Values live here (rather than being
// hardcoded in component logic) so they can be tuned in one place; changing them is still
// a code change + deploy, not a runtime/config toggle.
export const THINKING_BUBBLE = Object.freeze({
  // Only show the bubble once the current TTS request has been outstanding this long.
  // (A superseding request re-arms this timer while the bubble is not yet shown; fine for
  // today's one-at-a-time pipeline.) Originally 800ms (TT-1166 analysis: ~38% trigger rate,
  // sub-100ms flashes <10%); raised to 1000ms so the bubble appears less often on shorter
  // TTS waits.
  TRIGGER_MS: 1000,
  // Once shown, keep the bubble up at least this long so it reads as a deliberate signal
  // rather than a glitch, even if audio starts almost immediately after it appears.
  MIN_HOLD_MS: 300,
  // Defense-in-depth only. Sits above the common real TTS/Howler ceilings (16s network
  // fetch cap + 10s Coach load watchdog) and under the 30s Phrase safeguard, so it only
  // evicts a request when no terminal event ever fires (a bug). It is NOT a strict
  // "can never preempt": the Coach playback watchdog can re-arm up to 30s, so a rare
  // "loaded but never played" request could be cleared a couple seconds early (harmless).
  // Note: background tabs clamp setTimeout, so in the (bug-only) "no terminal event ever
  // fires" case this can stretch well past 28s — acceptable for a last-resort backstop.
  FAILSAFE_MS: 28000,
});
